/**
 * Pay-cycle arithmetic shared by every page that steps through pay periods.
 *
 * The pay-cycle dashboard and the payroll manager both walk whole cycles
 * backwards and forwards; keeping the stepping in one place is what stops the
 * two pages disagreeing about where a fortnight starts.
 *
 * All arithmetic is done at UTC midnight. A `YYYY-MM-DD` here is a calendar
 * date, not an instant — doing the maths in the browser's own zone makes the
 * result depend on where the operator is sitting, and past UTC+12 it lands on
 * the wrong day.
 */

export type PayPeriodMode = 'weekly' | 'fortnightly' | 'custom';

export interface PayPeriod {
  startDate: string;
  endDate: string;
}

/** Days in one cycle of `mode`. `custom` has no cycle, so it keeps its own width. */
export function cycleLength(mode: PayPeriodMode): number {
  return mode === 'fortnightly' ? 14 : 7;
}

export function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  return Math.round(
    (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000,
  );
}

/**
 * Step a period one whole cycle in `direction`.
 *
 * A custom range is moved by its own width rather than by a fixed 7 or 14, so
 * stepping an arbitrary window still lands edge-to-edge with no gap or overlap.
 */
export function stepPayPeriod(
  period: PayPeriod,
  mode: PayPeriodMode,
  direction: 'prev' | 'next',
): PayPeriod {
  const width =
    mode === 'custom'
      ? daysBetween(period.startDate, period.endDate) + 1
      : cycleLength(mode);
  const offset = direction === 'prev' ? -width : width;
  return {
    startDate: addDays(period.startDate, offset),
    endDate: addDays(period.endDate, offset),
  };
}

/**
 * A pay cycle pinned to the calendar: the first day of any one period, and how
 * many days a period runs. Every other period follows from those two.
 */
export interface PayCycle {
  anchor: string;
  width: number;
}

/** The pay period `date` falls in. Dates before the anchor count backwards from it. */
export function payPeriodContaining(date: string, cycle: PayCycle): PayPeriod {
  // Math.floor, not truncation: the day before the anchor is in the period
  // before it, not in the anchor's own.
  const index = Math.floor(daysBetween(cycle.anchor, date) / cycle.width);
  const startDate = addDays(cycle.anchor, index * cycle.width);
  return { startDate, endDate: addDays(startDate, cycle.width - 1) };
}

/**
 * How far `period` sits from the cycle containing `today`, in whole cycles.
 * 0 = the current cycle, -1 = the one just finished, 1 = the next one.
 *
 * Returns null for a custom range, where "which cycle is this" has no answer.
 */
export function cycleOffsetFromToday(
  period: PayPeriod,
  mode: PayPeriodMode,
  today: string,
): number | null {
  if (mode === 'custom') return null;
  const width = cycleLength(mode);
  const days = daysBetween(period.startDate, today);
  // A negative remainder must still floor away from zero, so use Math.floor
  // rather than truncation: 1 day before the start is -1 cycle, not 0.
  const cycles = Math.floor(days / width);
  return cycles === 0 ? 0 : -cycles; // negating 0 gives -0, which reads oddly

}

/** Plain-English name for a cycle offset, or null when there is nothing to say. */
export function cycleOffsetLabel(offset: number | null): string | null {
  if (offset === null) return null;
  if (offset === 0) return 'Current cycle';
  if (offset === -1) return 'Last cycle';
  if (offset === 1) return 'Next cycle';
  if (offset < 0) return `${Math.abs(offset)} cycles ago`;
  return `${offset} cycles ahead`;
}
